<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import type { PremadeTrip } from '@apps/main/new-desktop/core/models';
import { PremadeTripType } from '@apps/main/new-desktop/core/models';

import { AppTag } from '@libs/ui';

import ReadMoreButton from './ReadMoreButton.vue';

interface PremadeTripHeaderDesktopProps {
  tripDetails: PremadeTrip;
}

const props = defineProps<PremadeTripHeaderDesktopProps>();

interface PremadeTripHeaderDesktopEmits {
  (eventName: 'show-full-description'): void;
}

const emit = defineEmits<PremadeTripHeaderDesktopEmits>();

const descriptionContainer = useTemplateRef('descriptionContainer');
const columnsRef = useTemplateRef('columnsRef');

const shouldShowReadMoreButton = computed(() => {
  if (descriptionContainer.value && columnsRef.value) {
    return (
      descriptionContainer.value.offsetHeight > columnsRef.value.offsetHeight
    );
  }
  return false;
});
</script>

<template>
  <div class="premade-trip-header-desktop">
    <div class="columns" ref="columnsRef">
      <div class="left-column">
        <img
          :src="props.tripDetails.imageUrl"
          class="image"
          :alt="$t('a11y.trip_image')"
        />
      </div>
      <div ref="descriptionContainer" class="right-column">
        <AppTag
          v-if="props.tripDetails.type === PremadeTripType.AI"
          class="ai-generated-tag"
          type="info"
          :icon="{ name: 'ai', size: '16' }"
        >
          <template #title>{{ $t('planner.ai_generated') }}</template>
        </AppTag>
        <div class="heading">{{ props.tripDetails.headline }}</div>
        <slot name="description" />

        <div v-if="shouldShowReadMoreButton" class="read-more-button-wrapper">
          <ReadMoreButton @click="emit('show-full-description')" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.premade-trip-header-desktop {
  padding: var(--spacing-16);
  background-color: var(--color-white);
  border-radius: var(--border-radius-8);
  border: 1px solid var(--color-neutral-30);

  container-type: inline-size;
  container-name: premadeTripContainer;

  .columns {
    display: grid;
    grid-template-columns: 30% 1fr;
    grid-template-rows: min-content;
    gap: var(--spacing-16);
    height: 184px;
    @container premadeTripContainer (max-width: 925px) {
      grid-template-columns: 280px 1fr;
      height: 224px;
    }

    overflow: hidden;
    position: relative;

    .right-column {
      .ai-generated-tag {
        margin-bottom: var(--spacing-16);
        @include font-label-s-light;
        padding: var(--spacing-4) var(--spacing-8);
      }

      .heading {
        padding: var(--spacing-8) 0;
        @include font-title-label-l;
        color: var(--color-neutral-70);
      }

      .read-more-button-wrapper {
        position: absolute;
        left: calc(30% + var(--spacing-16));
        bottom: 0;
        width: 100%;
        @container premadeTripContainer (max-width: 925px) {
          left: calc(280px + var(--spacing-16));
        }
      }
    }

    .left-column {
      .image {
        border-radius: var(--border-radius-8);
        width: 100%;
        height: auto;
        object-fit: cover;
        @container premadeTripContainer (max-width: 925px) {
          aspect-ratio: 5/4;
        }
      }
    }
  }
}
</style>
